import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Table, { Column } from '../components/Table';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import ConfirmDialog from '../components/ConfirmDialog';
import { userService } from '../services/adminApi';
import { useAuth } from '../contexts/AuthContext';

interface UserRow {
  id: string;
  email: string;
  name?: string;
  employee_code?: string;
  employee_name?: string;
  mobile_number?: string;
  is_active: boolean;
  must_change_password?: boolean;
  display_name?: string;
}

interface ResetResult {
  id: string;
  email: string;
  name?: string | null;
  newPassword: string;
  emailed: boolean;
}

export default function UserList() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState<'active' | 'inactive'>('active');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deactivateTarget, setDeactivateTarget] = useState<UserRow | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // Password reset
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [resetTarget, setResetTarget] = useState<UserRow | 'bulk' | null>(null);
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetResults, setResetResults] = useState<ResetResult[] | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await userService.list(tab === 'active');
      const rows: UserRow[] = res.data.users || [];
      setUsers(rows.map((u) => ({ ...u, display_name: u.employee_name || u.name || '' })));
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await userService.deactivate(deactivateTarget.id);
      setMessage('User deactivated');
      setDeactivateTarget(null);
      load();
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (id: string) => {
    await userService.reactivate(id);
    setMessage('User reactivated');
    load();
  };

  const openReset = (target: UserRow | 'bulk') => {
    setResetTarget(target);
    setUseCustomPassword(false);
    setCustomPassword('');
    setResetError('');
    setResetResults(null);
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    const password = useCustomPassword ? customPassword : undefined;
    if (useCustomPassword && customPassword.trim().length < 8) {
      return setResetError('Password must be at least 8 characters, including a letter and a number');
    }

    setResetting(true);
    setResetError('');
    try {
      if (resetTarget === 'bulk') {
        const res = await userService.bulkResetPassword({ userIds: selectedIds, newPassword: password });
        setResetResults(res.data.results || []);
        setMessage(res.data.message);
      } else {
        const res = await userService.resetPassword(resetTarget.id, password);
        setResetResults([res.data]);
        setMessage(res.data.message);
      }
      setSelectedIds([]);
      load();
    } catch (err: any) {
      setResetError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const columns: Column<UserRow>[] = [
    { key: 'employee_code', header: 'Employee Code', render: (r) => r.employee_code || '—', sortable: true },
    {
      key: 'display_name',
      header: 'Name',
      render: (r) => r.employee_name || r.name || '—',
      sortable: true,
    },
    { key: 'email', header: 'Email', sortable: true },
    { key: 'mobile_number', header: 'Mobile', render: (r) => r.mobile_number || '—', sortable: true },
    {
      key: 'must_change_password',
      header: 'Password',
      sortable: true,
      searchAccessor: (r) => (r.must_change_password ? 'pending reset' : 'set'),
      render: (r) =>
        r.must_change_password ? (
          <span
            className="text-xs bg-amber-100 text-amber-800 rounded px-2 py-0.5"
            title="This user has a temporary password and will be asked to change it at next login"
          >
            Change pending
          </span>
        ) : (
          <span className="text-xs text-gray-400">Set by user</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-3">
          <Link to={`/admin/users/${row.id}/edit`} className="text-blue-600 hover:underline text-sm">
            Edit
          </Link>
          <button onClick={() => openReset(row)} className="text-blue-600 hover:underline text-sm">
            Reset Password
          </button>
          {tab === 'active' ? (
            <button onClick={() => setDeactivateTarget(row)} className="text-red-600 hover:underline text-sm">
              Deactivate
            </button>
          ) : (
            <button onClick={() => handleReactivate(row.id)} className="text-green-600 hover:underline text-sm">
              Reactivate
            </button>
          )}
        </div>
      ),
    },
  ];

  // The backend refuses to reset the requesting admin's own password (it would
  // invalidate the session doing the resetting), so don't offer it here either.
  const selectedExcludingSelf = selectedIds.filter((id) => id !== currentUser?.id);
  const resetTargetLabel =
    resetTarget === 'bulk'
      ? `${selectedExcludingSelf.length} selected user(s)`
      : resetTarget?.display_name || resetTarget?.email || '';

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User List</h1>
        <div className="flex gap-3">
          {tab === 'active' && (
            <Button
              variant="secondary"
              disabled={selectedExcludingSelf.length === 0}
              onClick={() => openReset('bulk')}
            >
              Reset Password ({selectedExcludingSelf.length})
            </Button>
          )}
          <Link to="/admin/users/new">
            <Button>Create User</Button>
          </Link>
        </div>
      </div>

      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-4 py-2">{message}</div>}

      <div className="flex gap-4 border-b mb-4">
        <button
          onClick={() => setTab('active')}
          className={`pb-2 text-sm font-medium ${tab === 'active' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
        >
          Active Users
        </button>
        <button
          onClick={() => setTab('inactive')}
          className={`pb-2 text-sm font-medium ${tab === 'inactive' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
        >
          Inactive Users
        </button>
      </div>

      <Table
        columns={columns}
        rows={users}
        loading={loading}
        emptyMessage="No users"
        searchable
        searchPlaceholder="Search by name, code, email, mobile..."
        selectable={tab === 'active'}
        selectedKeys={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <ConfirmDialog
        isOpen={!!deactivateTarget}
        title="Deactivate User"
        message={`Deactivate ${deactivateTarget?.display_name || deactivateTarget?.email}? They will no longer be able to log in.`}
        confirmLabel="Deactivate"
        variant="danger"
        loading={deactivating}
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      />

      <Modal
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        title={resetResults ? 'Password Reset' : 'Reset Password'}
      >
        {resetResults ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Each user must choose a new password the next time they log in — these temporary passwords
              cannot be used for anything else.
            </p>
            <div className="max-h-72 overflow-y-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-left px-3 py-2">Temporary password</th>
                    <th className="text-left px-3 py-2">Emailed</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {resetResults.map((r) => (
                    <tr key={r.id}>
                      <td className="px-3 py-2">{r.name || r.email}</td>
                      <td className="px-3 py-2 font-mono">{r.newPassword}</td>
                      <td className="px-3 py-2">
                        {r.emailed ? (
                          <span className="text-green-700">Sent</span>
                        ) : (
                          <span className="text-amber-700">Not sent — share manually</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setResetTarget(null)}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Reset the password for <strong>{resetTargetLabel}</strong>. They will be emailed a temporary
              password and prompted to choose their own the next time they log in.
            </p>

            {resetError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                {resetError}
              </div>
            )}

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={useCustomPassword}
                onChange={(e) => setUseCustomPassword(e.target.checked)}
              />
              <span>
                Set a specific temporary password instead of generating one
                {resetTarget === 'bulk' && (
                  <span className="block text-xs text-gray-500">
                    All selected users would share this password — leave unchecked to give each their own.
                  </span>
                )}
              </span>
            </label>

            {useCustomPassword && (
              <Input
                id="customPassword"
                label="Temporary password"
                type="text"
                value={customPassword}
                onChange={(e) => setCustomPassword(e.target.value)}
                placeholder="At least 8 characters, with a letter and a number"
              />
            )}

            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setResetTarget(null)} disabled={resetting}>
                Cancel
              </Button>
              <Button onClick={handleReset} loading={resetting}>
                Reset Password
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
